/**
 * useSim.ts — the single bridge between a BackendAdapter and React.
 *
 * Owns the adapter lifecycle (load → subscribe → start/stop → dispose), runs
 * the engine's pure `step()` on each inbound tick, keeps the authoritative
 * ModelState in a ref (so the tick loop never blocks on React), and
 * publishes a throttled snapshot to React state for the UI to render.
 *
 * Why a ref + throttle instead of setState per tick: the animation demos in
 * this monorepo (vacuum-demo, fishing-demo) deliberately keep their state out
 * of React because "React would fight the animation loop". The same applies
 * here — at 4 Hz it's not as severe as 60 FPS, but the principle holds: the
 * sim state is high-frequency, the UI is low-frequency. Decouple them.
 *
 * The published snapshot is the union of (a) the engine's ModelState and
 * (b) the derived balance sheet + metrics, so consumers don't re-derive. The
 * GraphCanvas reads the snapshot's tick for edge flows + annotations; the
 * Sidebar reads the balance sheet + metrics.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { LazyLocalAdapter, emptyMainBundle, type BackendAdapter } from './engine/adapter';
import { ApiAdapter, type ConnectionStatus } from './engine/ApiAdapter';
import { EditHistory, withEditHistory, type HistoryControls } from './engine/history';
import {
  buildBalanceSheet,
  buildGroupBalanceSheet,
  computeGroupMetrics,
  computeMetrics,
  type BalanceSheet,
  type GroupBalance,
} from './engine/balance';
import { initialState, step } from './engine/sim';
import type {
  GraphEdge,
  GraphNode,
  KnobPin,
  LegendEntry,
  MetricSpec,
  ModelState,
  NodeGroup,
  ScenarioBundle,
  TickPayload,
  WorkflowTrigger,
} from './engine/types';
import { memberIdsOf } from './graph/groups';
import {
  deriveOverlays,
  toActivityEntries,
  type AgentActivityEntry,
  type DecisionEntry,
  type OverlayState,
} from './overlays/annotations';

/** The throttled snapshot the UI renders from. */
export interface SimSnapshot {
  /** The active graph's bundle — what the canvas + sidebar render. Aliases
   *  `bundles.find(id === activeGraphId)`. Kept for the many `sim.bundle.graph`
   *  readers; new code that needs ALL graphs reads `bundles`. */
  bundle: ScenarioBundle;
  /** Every graph in the project (Main first). Charts + the field menu read
   *  this to surface cross-graph references (node ids are globally unique). */
  bundles: ScenarioBundle[];
  /** The active graph id (which tab is shown). Per-client UI state. */
  activeGraphId: string;
  state: ModelState;
  balance: BalanceSheet;
  metrics: Record<string, number>;
  /** Per-node-group aggregates, keyed by group id. Membership is recomputed
   *  from the current bundle positions on every publish (spatial, not stored).
   *  Empty when the bundle has no node groups. */
  groupBalance: Record<string, GroupBalance>;
  groupMetrics: Record<string, Record<string, number>>;
  /** Per-node/edge overlay cues derived (with hysteresis) from the tick's
   *  annotations. The canvas + decision feed render from this. */
  overlays: OverlayState;
  /** The agent activity log — decisions + agent debug logs folded from every
   *  applied tick (bounded ring, newest last). The DecisionsModal renders
   *  this; `overlays.decisions` remains only the last-8 per-tick cue channel.
   *  Local in-browser mode has no agents, so it stays empty there. */
  decisionLog: AgentActivityEntry[];
  /** Recent ticks retained in-memory (bounded to HISTORY_MAX). Plots + the
   *  dashboard read this to draw time-series without a backend round-trip per
   *  refresh. Oldest-first; empty until the first tick lands. */
  recentTicks: TickPayload[];
  /** 'idle' before the first tick lands, 'running' while streaming, 'stopped'
   *  after the user pauses. The adapter owns streaming; this mirrors it. */
  status: 'idle' | 'running' | 'stopped';
  /** Undo/redo availability for the edit history (see engine/history.ts).
   *  False in demo mode and before any edit lands. */
  canUndo: boolean;
  canRedo: boolean;
  /** Latest error from the adapter (load failure, WS drop). Null on success. */
  error: string | null;
}

export interface UseSimResult extends SimSnapshot {
  start: () => void;
  stop: () => void;
  reset: () => void;
  /** Clear the in-canvas error banner (`error` on the snapshot). */
  dismissError: () => void;
  setParam: (path: string, value: number) => void;
  setParamKey?: (target: 'node' | 'edge', id: string, oldKey: string, newKey: string) => void;
  /** Undo/redo the last graph edit (snapshot history — engine/history.ts).
   *  Present only on editable sessions; no-op when the stack is empty. */
  undo?: () => void;
  redo?: () => void;
  /** Editor methods. Present only when the adapter is editable (LocalAdapter).
   *  The UI feature-detects `result.canEdit` before showing editor controls. */
  canEdit: boolean;
  /** Which execution surface the sim is running on. The UI reads this to
   *  distinguish read-only demo playback from a live editable/relay session —
   *  e.g. grey out knobs in demo mode instead of letting `setParam` silently
   *  no-op in read-only local mode; swap play/pause for a "Listening for events"
   *  indicator in relay mode. Mirrors the execution surfaces in
   *  `docs/ARCHITECTURE.md`. `'relay'` is a refinement of `'api'`: same
   *  adapter (ApiAdapter over WS), but the backend starts no clock and ticks
   *  arrive only from external POSTs. */
  mode: 'demo' | 'edit' | 'api' | 'relay';
  addNode?: (node: GraphNode) => void;
  updateNode?: (node: GraphNode) => void;
  removeNode?: (nodeId: string) => void;
  duplicateNode?: (nodeId: string) => string | null;
  renameNode?: (oldId: string, newId: string) => boolean;
  addEdge?: (edge: GraphEdge) => void;
  updateEdge?: (edge: GraphEdge) => void;
  removeEdge?: (edgeId: string) => void;
  renameEdge?: (oldId: string, newId: string) => boolean;
  setEdgeGroup?: (edgeId: string, group: string | undefined) => void;
  updateEdgeHandles?: (
    edgeId: string,
    next: { source?: string; target?: string; sourceHandle?: string; targetHandle?: string },
  ) => void;
  setBends?: (edgeId: string, bends: import('./engine/types').Bend[]) => void;
  setLabelT?: (edgeId: string, t: number) => void;
  addGroup?: (group: string) => void;
  addNodeGroup?: (group: NodeGroup) => void;
  updateNodeGroup?: (group: NodeGroup) => void;
  removeNodeGroup?: (groupId: string) => void;
  renameGroup?: (oldId: string, newId: string) => boolean;
  addMetric?: (metric: MetricSpec) => void;
  updateMetric?: (metric: MetricSpec) => void;
  removeMetric?: (key: string) => void;
  renameMetric?: (oldKey: string, newKey: string) => void;
  setLegend?: (legend: LegendEntry[]) => void;
  /** Replace the bundle's whole pinned-knobs list (sidebar "Knobs" section).
   *  Wholesale replace like setLegend; empty list stored as undefined. */
  setKnobs?: (knobs: KnobPin[]) => void;
  /** Replace the bundle's whole workflow-trigger list (the trigger
   *  contract). Wholesale replace like setLegend. */
  setWorkflowTriggers?: (triggers: WorkflowTrigger[]) => void;
  replaceBundle?: (bundle: ScenarioBundle) => void;
  /** Snapshot the current (possibly edited) bundle for File → Save. Present
   *  only on editable adapters (LocalAdapter / LazyLocalAdapter); gated by
   *  `canEdit`, like the other editor ops. */
  exportBundle?: () => ScenarioBundle | undefined;
  // ─── Multi-graph (graph collection) ──────────────────────────────────────
  /** Switch which graph the editor canvas shows. Per-client UI state. */
  setActiveGraph?: (graphId: string) => void;
  /** Add a new graph (collection op). Returns the minted graph id. */
  addGraph?: (name: string) => string;
  /** Remove a graph by id (refuses Main). */
  removeGraph?: (graphId: string) => void;
  /** Rename a graph (re-slugs the id for non-Main). Returns the new id. */
  renameGraph?: (graphId: string, name: string) => string;
  /** Live WS connection status. `'disconnected'` for the local adapters
   *  (no socket); the real states only fire for the ApiAdapter. */
  connectionStatus: ConnectionStatus;
  /** POST a tick to the relay webhook (Phase D). Only meaningful in relay
   *  mode; the dev "POST test event" tool calls this. No-op when the adapter
   *  isn't an ApiAdapter or the base URL can't be derived (a WS URL with no
   *  http(s) equivalent). Returns the response status or null on no-op. */
  postEvent?: (tick: TickPayload) => Promise<number | null>;
  /** Export the captured/replayed run from the backend (Phase E). Fetches the
   *  wide/long CSV or trajectory JSONL from ``GET /api/scenarios/{id}/export``
   *  and triggers a browser download. Returns the HTTP status or null when no
   *  API source is wired (local/edit modes have no backend to export from —
   *  those callers use the in-app bundle-JSON download instead). */
  exportRun?: (format: 'wide' | 'long' | 'trajectory', opts?: { from?: number; to?: number; reward?: string }) => Promise<number | null>;
  /** Download the project as a ZIP (graph + manifest + code). No-op when no
   *  API source is wired. Returns the HTTP status or null. */
  downloadProject?: () => Promise<number | null>;
  /** Arm/disarm/clear the live capture ring buffer (Phase E). When armed,
   *  the relay webhook records every post-agent tick for export. No-op when
   *  no API source is wired. */
  setCapture?: (action: 'arm' | 'disarm' | 'clear', opts?: { label?: string }) => Promise<void>;
  /** Latest capture state polled from the backend (Phase E). ``null`` when no
   *  API source is wired or before the first poll lands. The sidebar badge
   *  reads ``captured`` + ``armed``. */
  captureState: { armed: boolean; captured: number; evicted: number } | null;
  /** Demo tables snapshot (Phase J). ``null`` when no API source is wired or
   *  before the first load. The TablesPanel renders + edits this. Each entry
   *  is one demo table (orders/staff/inventory/params) with a CAPPED row
   *  window (the route returns the first ~100 rows + the true ``count`` —
   *  log tables like events/snapshots grow unboundedly; the Data tab's paged
   *  route owns full browsing). */
  tablesState: { name: string; columns: { name: string; type: string; required?: boolean; default?: unknown }[]; rows: { id: number | string; cells: Record<string, string | number | boolean | null> }[]; count?: number }[] | null;
  /** Last tables error (failed load/PATCH/insert/delete) — rendered as a
   *  banner by the TablesPanel. Null when the last operation succeeded. */
  tablesError: string | null;
  clearTablesError: () => void;
  /** Enable/disable the ~3s tables refresh poll (the App drives this: on
   *  ONLY while the dock panel is visible — db knobs pull once on mount
   *  instead of keeping a poll alive). */
  setTablesPolling: (on: boolean) => void;
  loadTables?: () => Promise<void>;
  updateTableCell?: (table: string, rowId: number | string, column: string, value: string | number | boolean | null) => Promise<void>;
  addTable?: (name: string, columns: { name: string; type: string }[]) => Promise<void>;
  addTableRow?: (table: string, cells: Record<string, string | number | boolean | null>) => Promise<void>;
  deleteTableRow?: (table: string, rowId: number | string) => Promise<void>;
  /** Start the project's backend model subprocess (the manifest's ``run``
   *  command). The model POSTs ticks to the relay webhook. No-op when no API
   *  source is wired. Hits ``POST /api/projects/{id}/run/start``. */
  runStart?: () => Promise<void>;
  /** Stop the project's backend model subprocess. No-op when no API source is
   *  wired. Hits ``POST /api/projects/{id}/run/stop``. */
  runStop?: () => Promise<void>;
  /** Toggle the run-level agents kill-switch (the emergency stop for agent
   *  writes — docs/TRAINING.md §2). No-op when no API source is wired. Hits
   *  ``POST /api/projects/{id}/agents/toggle``. */
  toggleAgents?: (enabled: boolean) => Promise<void>;
  /** Project model run status, polled from ``GET /api/projects/{id}/run``.
   *  ``null`` when no API source is wired or before the first poll. The
   *  transport bar's Play/Pause reflects `running`. */
  runStatus: {
    running: boolean;
    pid: number | null;
    exit_code: number | null;
    /** The run-level agents kill-switch (absent on older backends). */
    agentsEnabled?: boolean;
  } | null;
  /** Subscribe to workflow step events (the worker gate's I/O captures,
   *  broadcast over the project WS as `workflowEvent`). Returns an
   *  unsubscribe fn. API/relay sessions only — local adapters and the loading
   *  sentinel omit it. The Workflows tab merges these into its live view. */
  onWorkflowEvent?: (cb: (m: import('./engine/types').WorkflowEventMessage) => void) => () => void;
  /** Subscribe to ⚡ trigger fire/fail events (broadcast as `triggerEvent`
   *  by the relay path — one frame per declarative-trigger or agent-grant
   *  fire attempt). API/relay sessions only. The TriggersPanel + node badges
   *  render live trigger state from these. */
  onTriggerEvent: (cb: (m: import('./engine/types').TriggerEventMessage) => void) => () => void;
  /** Live ⚡ trigger status: {triggerId: {lastFiredStep, lastError, at}}.
   *  Hydrated from GET /workflows/triggers/status on connect, then updated
   *  by `triggerEvent` frames. API/relay sessions only. */
  triggerStatus: Record<string, { lastFiredStep: number | null; lastError: string | null; at: number }>;
}

/** Minimum gap between React re-renders, in ms. At 4 Hz ticks arrive every
 *  250 ms; rendering on every tick is fine, but this guards against a future
 *  higher-Hz WS backend flooding React. 60 ms ≈ 16 FPS render budget. */
const RENDER_THROTTLE_MS = 60;

/** Max ticks retained in the in-memory history buffer (`recentTicks`). Plots
 *  read this to draw time-series without a backend round-trip per refresh.
 *  Bounded so a long-running session doesn't grow unbounded — plots that need
 *  longer history hit the capture/export surface. ~500 ticks ≈ 2 min at 4 Hz. */
const HISTORY_MAX = 500;

/** Max entries retained in the agent activity log (`decisionLog`). Same
 *  order of magnitude as the tick history; deeper history is the dataset /
 *  replay surface, not the live buffer. */
const ACTIVITY_MAX = 500;

/** Derive the HTTP origin from a WS URL — stripping the scheme swap (ws→http,
 *  wss→https) AND any path/query (e.g. ``ws://host:8000/ws?key=…`` →
 *  ``http://host:8000``). REST endpoints (capture, export, tables, events) are
 *  mounted at the origin's ``/api/…`` root, not under the WS path. */
function httpOriginOf(wsUrl: string): string | null {
  try {
    const u = new URL(wsUrl);
    const scheme = u.protocol === 'wss:' ? 'https:' : 'http:';
    return `${scheme}//${u.host}`;
  } catch {
    return null;
  }
}

export function useSim(
  scenarioId: string,
  opts: {
    editable?: boolean;
    hz?: number;
    /** When `'api'`, construct an ApiAdapter (WebSocket) instead of a local
     *  adapter. `apiUrl` is required in that mode; `apiKey` is optional
     *  (keyless backends are a supported setup). */
    source?: 'local' | 'api';
    /** When `'relay'` AND `source==='api'`, ask the backend for relay mode
     *  (no demo pump; ticks arrive from external POSTs). Ignored otherwise.
     *  Phase D. */
    relay?: boolean;
    apiUrl?: string;
    apiKey?: string;
  } = {},
): UseSimResult {
  const adapterRef = useRef<BackendAdapter | null>(null);
  /** The history-wrapped adapter when the session is editable (see the mount
   *  effect); null for demo sessions. undo/redo + canUndo/canRedo read this. */
  const historyAdapterRef = useRef<(BackendAdapter & HistoryControls) | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const stateRef = useRef<ModelState | null>(null);
  const bundleRef = useRef<ScenarioBundle | null>(null);
  /** The full graph collection (all graphs). Charts + the field menu read this
   *  for cross-graph references. `bundleRef` is the active entry's view. */
  const bundlesRef = useRef<ScenarioBundle[]>([]);
  const activeGraphIdRef = useRef<string>('main');
  const lastRenderRef = useRef(0);
  // Pending tick waiting to be applied + rendered. Held in a ref so the
  // subscriber callback (stable) can write it without re-subscribing.
  const pendingTickRef = useRef<TickPayload | null>(null);
  // Overlay hysteresis state — persists across ticks so a node must be flagged
  // bottleneck for N consecutive ticks before the cue fires, and the decision
  // feed retains a short history.
  const bnCountsRef = useRef<Map<string, number>>(new Map());
  const decisionsRef = useRef<DecisionEntry[]>([]);
  const lastOverlayStepRef = useRef(-1);
  // Agent activity ring (decisions + agentLogs folded per tick, newest
  // last) — the DecisionsModal's live source. Deduped on step: the engine
  // silently drops out-of-order ticks, but the subscriber still fires, and a
  // re-delivered tick must not double-enter the log.
  const activityRef = useRef<AgentActivityEntry[]>([]);
  const lastActivityStepRef = useRef(-1);
  // Bounded ring of recent ticks for plots/dashboard time-series. Appended in
  // the subscriber; cleared on reset. Held in a ref (high-frequency writes)
  // and copied into the throttled snapshot publish.
  const historyRef = useRef<TickPayload[]>([]);
  /** Latest inbound param-change broadcast (relay path). Read by the relay UI
   *  to show "last knob move: kitchen.capacity → 12 (just now)". Not part of
   *  the snapshot — it doesn't drive a React re-render on its own. */
  const lastParamChangeRef = useRef<{ path: string; value: number; at: number } | null>(null);
  /** Workflow step-event subscribers (ApiAdapter's `workflowEvent` broadcast —
   *  the worker gate's I/O captures). The Workflows tab registers through
   *  `sim.onWorkflowEvent`; events merge into its per-run status state. */
  const workflowEventSubsRef = useRef(new Set<(m: import('./engine/types').WorkflowEventMessage) => void>());
  /** Subscribe to workflow step events. Declared BEFORE the early return
   *  below — a hook in the final return literal would be skipped on the
   *  loading path and change the hook count between renders (the Studio
   *  "Should have a queue" crash). Returns an unsubscribe fn. */
  const onWorkflowEvent = useCallback((cb: (m: import('./engine/types').WorkflowEventMessage) => void) => {
    workflowEventSubsRef.current.add(cb);
    return () => {
      workflowEventSubsRef.current.delete(cb);
    };
  }, []);
  /** Trigger fire/fail subscribers (ApiAdapter's `triggerEvent` broadcast —
   *  the relay path's ⚡ fire attempts). The TriggersPanel + node badge
   *  register through `sim.onTriggerEvent`. */
  const triggerEventSubsRef = useRef(new Set<(m: import('./engine/types').TriggerEventMessage) => void>());
  /** Subscribe to trigger fire/fail events (same declare-before-early-return
   *  discipline as onWorkflowEvent above). Returns an unsubscribe fn. */
  const onTriggerEvent = useCallback((cb: (m: import('./engine/types').TriggerEventMessage) => void) => {
    triggerEventSubsRef.current.add(cb);
    return () => {
      triggerEventSubsRef.current.delete(cb);
    };
  }, []);
  /** Live trigger status hydrated from the backend's run-state bookkeeping
   *  (GET /workflows/triggers/status) + updated by `triggerEvent` frames.
   *  {triggerId: {lastFiredStep, lastError, at}} — the TriggersPanel + node
   *  badges render it. */
  const [triggerStatus, setTriggerStatus] = useState<Record<string, { lastFiredStep: number | null; lastError: string | null; at: number }>>({});
  /** Capture state polled from the backend (Phase E). The sidebar badge reads
   *  this to show the live tick count while armed. Polled at a low cadence by
   *  the effect below; null when no API source is wired. */
  const [captureState, setCaptureState] = useState<{ armed: boolean; captured: number; evicted: number } | null>(null);
  /** Project tables snapshot. Loaded on demand by loadTables; null until first
   *  load or when no API source is wired. */
  const [tablesState, setTablesState] = useState<UseSimResult['tablesState']>(null);
  /** Latest tablesState mirror for the adapter-setup effect (which runs after
   *  mount but must replay any snapshot a previous session already loaded). */
  const tablesStateRef = useRef<UseSimResult['tablesState']>(null);
  tablesStateRef.current = tablesState;
  /** Last tables error (failed load/PATCH/insert/delete). Surfaced by the
   *  TablesPanel as a banner; cleared by clearTablesError or the next success. */
  const [tablesError, setTablesError] = useState<string | null>(null);
  const clearTablesError = useCallback(() => setTablesError(null), []);
  /** Mutation activity timestamp — the polling refresh backs off briefly after
   *  a write so optimistic splices aren't clobbered mid-edit. */
  const tablesActivityRef = useRef(0);
  /** Whether the tables poll runs (the App enables it while the panel is
   *  visible or a db knob exists). */
  const tablesPollRef = useRef(false);
  const setTablesPolling = useCallback((on: boolean) => {
    tablesPollRef.current = on;
  }, []);
  /** Project model run status, polled from /api/projects/{id}/run at a low
   *  cadence. The transport bar's Play/Pause reflects `running`. Null when no
   *  API source is wired. */
  const [runStatus, setRunStatus] = useState<UseSimResult['runStatus']>(null);

  const [snapshot, setSnapshot] = useState<SimSnapshot | null>(null);

  // Compute (or re-use) the OverlayState for the given tick. Idempotent per
  // step: if the publish fires multiple times for the same tick (e.g. the
  // start/stop force-publish), we don't double-count the bottleneck frames.
  const computeOverlay = useCallback(
    (tick: TickPayload): OverlayState => {
      const bundle = bundleRef.current;
      if (!bundle) {
        return { nodeCues: {}, edgeCues: {}, decisions: [] };
      }
      if (lastOverlayStepRef.current !== tick.step) {
        const { state, counts } = deriveOverlays(
          tick,
          bundle.graph,
          bnCountsRef.current,
          { prevDecisions: decisionsRef.current },
        );
        bnCountsRef.current = counts;
        decisionsRef.current = state.decisions;
        lastOverlayStepRef.current = tick.step;
        // Stash on the ref-closure so publish can read it without recomputing.
        lastOverlayRef.current = state;
      }
      return lastOverlayRef.current;
    },
    [],
  );
  const lastOverlayRef = useRef<OverlayState>({ nodeCues: {}, edgeCues: {}, decisions: [] });

  // Publish a snapshot to React iff enough time has passed since the last
  // publish. Called from the tick subscriber and from start/stop.
  const publish = useCallback(
    (status: SimSnapshot['status'], force = false) => {
      const now = performance.now();
      if (!force && now - lastRenderRef.current < RENDER_THROTTLE_MS) return;
      lastRenderRef.current = now;

      const bundle = bundleRef.current;
      const state = stateRef.current;
      if (!bundle || !state) return;
      const tick = pendingTickRef.current ?? state.lastTick;
      const balance = buildBalanceSheet(bundle.graph, tick);
      const metrics = computeMetrics(bundle.graph, tick, bundle.metrics);
      const overlays = computeOverlay(tick);
      // Per-group aggregates. Membership is spatial — recomputed each publish
      // from the current node positions, so dragging a node in/out of a group
      // updates its aggregates on the next tick without any edit op.
      const groups = bundle.nodeGroups ?? [];
      const groupBalance: Record<string, GroupBalance> = {};
      const groupMetrics: Record<string, Record<string, number>> = {};
      for (const g of groups) {
        const memberIds = memberIdsOf(g, bundle.graph.nodes);
        groupBalance[g.id] = buildGroupBalanceSheet(bundle.graph, tick, memberIds);
        groupMetrics[g.id] = computeGroupMetrics(bundle.graph, tick, g, memberIds);
      }
      setSnapshot({
        bundle,
        bundles: bundlesRef.current,
        activeGraphId: activeGraphIdRef.current,
        state,
        balance,
        metrics,
        groupBalance,
        groupMetrics,
        overlays,
        // A fresh array per publish (the buffer itself is mutated in place) —
        // consumers memoize on recentTicks identity (chart field menu, canvas
        // effects) and must see each publish as a change.
        recentTicks: [...historyRef.current],
        // Same fresh-copy discipline for the activity ring (the modal
        // memoizes on identity).
        decisionLog: [...activityRef.current],
        status,
        canUndo: historyAdapterRef.current?.canUndo() ?? false,
        canRedo: historyAdapterRef.current?.canRedo() ?? false,
        error: null,
      });
    },
    [computeOverlay],
  );

  // Load + subscribe on mount; dispose on unmount.
  useEffect(() => {
    let disposed = false;
    let adapter: BackendAdapter;
    if (opts.source === 'api') {
      if (!opts.apiUrl) {
        throw new Error("source='api' requires apiUrl");
      }
      const api = new ApiAdapter({
        url: opts.apiUrl,
        apiKey: opts.apiKey,
        scenarioId,
        hz: opts.hz ?? 4,
        mode: opts.relay ? 'relay' : 'demo',
      });
      // Subscribe to connection status before loadScenario so the UI sees the
      // initial "connecting" → "connected" transitions.
      api.onConnection((s) => {
        if (!disposed) setConnectionStatus(s);
      });
      // Surface inbound knob-change broadcasts (peer setParam or agent delta
      // on the relay path). The local adapters don't broadcast — they apply
      // setParam directly — so this is ApiAdapter-only. We don't apply the
      // change to anything here; consumers that need the live param state
      // (a future "Listening for events" panel, an external-model driver)
      // subscribe via the adapter directly. Stash the latest on a ref so the
      // relay UI can render a ticker without re-deriving.
      api.onParamChange((path, value) => {
        if (disposed) return;
        lastParamChangeRef.current = { path, value, at: performance.now() };
      });
      // Workflow step events (worker gate I/O captures) fan out to whoever
      // subscribed via sim.onWorkflowEvent — the Workflows tab's live view.
      api.onWorkflowEvent((msg) => {
        if (disposed) return;
        for (const cb of workflowEventSubsRef.current) cb(msg);
      });
      // Trigger fire/fail events (the relay path's ⚡ fire attempts) fan out
      // to sim.onTriggerEvent subscribers AND merge into the live
      // triggerStatus map — the TriggersPanel + node badges render it.
      api.onTriggerEvent((msg) => {
        if (disposed) return;
        setTriggerStatus((cur) => ({
          ...cur,
          [msg.triggerId]: {
            lastFiredStep: msg.status === 'fired' ? msg.step : cur[msg.triggerId]?.lastFiredStep ?? null,
            lastError: msg.status === 'failed' ? msg.error ?? 'unknown error' : null,
            at: Date.now(),
          },
        }));
        for (const cb of triggerEventSubsRef.current) cb(msg);
      });
      // Relay hydrate: adopt the backend's current stocks + step as the
      // baseline when connecting mid-run. Without this, the first received
      // tick fails conservation (client prev=0 vs backend's accumulated state).
      api.onStateHydrate((step, stocks) => {
        if (disposed) return;
        const prev = stateRef.current;
        const bundle = bundleRef.current;
        if (!prev || !bundle) return;
        // Adopt the snapshot's stocks for every node the backend sent; nodes
        // it omitted keep their current (initial) value. Step is set so the
        // next tick (step+1) passes the `tick.step > prev.step` ordering guard.
        const nextStocks = { ...prev.stocks };
        for (const [id, s] of Object.entries(stocks)) nextStocks[id] = s;
        stateRef.current = { step, stocks: nextStocks, lastTick: prev.lastTick };
        publish('idle', true);
      });
      adapter = api;
    } else {
      // Read-only local view: the same lazy local engine (localStorage
      // bundle → compute), with canEdit gating the editor UI off.
      adapter = new LazyLocalAdapter(scenarioId, opts.hz ?? 4);
    }
    // Wrap editable sessions with snapshot undo/redo (Ctrl+Z / Ctrl+Shift+Z).
    // The wrapper intercepts every editor op and records a pre-mutation
    // snapshot; undo replays through the adapter's own `replaceBundle` op, so
    // it works identically for LocalAdapter and the WS ApiAdapter. Demo
    // sessions get no history. A fresh effect run builds a fresh
    // adapter — and therefore a fresh history; stale snapshots from a previous
    // session must never apply to a new one.
    if (opts.source === 'api' || opts.editable) {
      const wrapped = withEditHistory(adapter, new EditHistory());
      historyAdapterRef.current = wrapped;
      adapter = wrapped;
    } else {
      historyAdapterRef.current = null;
    }
    adapterRef.current = adapter;
    // A tables snapshot may already be loaded (re-mount on graph switch) —
    // replay it so the local engine's cell bindings resolve immediately.
    adapter.updateTableSnapshots?.(tablesStateRef.current ?? null);

    // Load failed (backend rejected the graph, network error, …). Seed the
    // empty Main graph so the whole UI stays alive: the canvas renders empty
    // and the error shows inside it instead of blocking the app. If the load
    // promise resolves later, the .then overwrites this with the real bundle.
    const failLoad = (msg: string) => {
      const bundle = emptyMainBundle();
      bundleRef.current = bundle;
      bundlesRef.current = [bundle];
      activeGraphIdRef.current = bundle.id;
      stateRef.current = initialState(bundle);
      historyRef.current = [];
      publish('idle', true);
      setSnapshot((s) => (s ? { ...s, error: msg } : s));
    };

    adapter
      .loadScenario()
      .then((project) => {
        if (disposed) return;
        const graphs = project.graphs;
        bundlesRef.current = graphs;
        // Main is graphs[0] (the load helpers guarantee this); adopt it as the
        // active view unless the adapter already tracks a different active id.
        const adapterActive = adapter.activeGraphId?.() ?? graphs[0].id;
        activeGraphIdRef.current = graphs.some((b) => b.id === adapterActive)
          ? adapterActive
          : graphs[0].id;
        const bundle = graphs.find((b) => b.id === activeGraphIdRef.current) ?? graphs[0];
        bundleRef.current = bundle;
        stateRef.current = initialState(bundle);
        // Force-publish the idle frame so the UI shows the graph immediately.
        publish('idle', true);
      })
      .catch((e: unknown) => {
        if (disposed) return;
        failLoad(e instanceof Error ? e.message : String(e));
      });

    // Subscribe to bundle changes (LocalAdapter fires these when the user
    // edits — add/remove nodes/edges/metrics, or switches/adds/removes a graph).
    // On each change, refresh the collection + the active bundle + re-derive
    // state so the next publish reflects the new graph.
    const unsubBundle = adapter.onBundleChange?.(() => {
      if (disposed) return;
      // Refresh the full collection (covers add/remove/rename graph + per-graph
      // edits on any graph), then re-derive the active view.
      const project = adapter.getProject?.();
      if (project && project.graphs.length > 0) {
        bundlesRef.current = project.graphs;
        // Keep the active id valid (a rename/remove may have invalidated it).
        const adapterActive = adapter.activeGraphId?.() ?? activeGraphIdRef.current;
        activeGraphIdRef.current = project.graphs.some((b) => b.id === adapterActive)
          ? adapterActive
          : project.graphs[0].id;
      }
      const next = adapter.exportBundle?.();
      if (!next) return;
      bundleRef.current = next;
      // Resize stocks to cover any new nodes (default 0); keep existing stocks.
      const stocks = { ...stateRef.current!.stocks };
      for (const n of next.graph.nodes) if (!(n.id in stocks)) stocks[n.id] = next.initialStocks?.[n.id] ?? 0;
      stateRef.current = { ...stateRef.current!, stocks };
      // Force-publish so the editor sees its change immediately.
      lastRenderRef.current = 0;
      publish('idle', true);
    });

    // Surface engine errors (CycleError from a feedback loop, ExprError from
    // a bad formula) as the sim's error banner.
    const unsubError = adapter.onError?.((msg) => {
      if (disposed) return;
      const bundle = bundleRef.current;
      const st = stateRef.current;
      if (!bundle || !st) {
        // Error arrived before the scenario finished loading (e.g. the backend
        // rejected the graph). Seed the empty canvas + show the error in it
        // rather than crashing on null refs or blocking the whole UI.
        failLoad(msg);
        return;
      }
      setSnapshot((s) => ({
        ...(s ?? {
          bundle,
          bundles: bundlesRef.current,
          activeGraphId: activeGraphIdRef.current,
          state: st,
          balance: buildBalanceSheet(bundle.graph, st.lastTick),
          metrics: {},
          groupBalance: {},
          groupMetrics: {},
          overlays: { nodeCues: {}, edgeCues: {}, decisions: [] },
          recentTicks: [],
          decisionLog: [],
          status: 'stopped',
          canUndo: false,
          canRedo: false,
          error: null,
        }),
        status: 'stopped',
        error: msg,
      }));
    });

    const unsub = adapter.subscribe((tick) => {
      if (disposed) return;
      const bundle = bundleRef.current;
      const prev = stateRef.current;
      if (!bundle || !prev) return;
      // Engine does conservation + ordering checks; on error, surface it.
      try {
        let currentPrev = prev;
        if (tick.step === 1 && prev.step > 0) {
          // Fresh run started from the backend (or restarted). Wipe the old
          // frontend history so conservation checks and plots start clean.
          currentPrev = initialState(bundle);
          stateRef.current = currentPrev;
          historyRef.current = [];
          lastOverlayStepRef.current = -1;
          bnCountsRef.current.clear();
          decisionsRef.current = [];
          activityRef.current = [];
          lastActivityStepRef.current = -1;
        }

        stateRef.current = step(currentPrev, tick, bundle.graph);
        pendingTickRef.current = tick;
        // Append to the in-memory history for plot time-series. Bounded —
        // shift the oldest once over HISTORY_MAX. Cheap (ref write, no render);
        // the snapshot publish copies the array at the throttled cadence.
        const h = historyRef.current;
        h.push(tick);
        if (h.length > HISTORY_MAX) h.shift();
        // Fold the tick's decisions + agentLogs into the activity ring (the
        // DecisionsModal's live source). Same bounded-ring discipline.
        if (tick.step > lastActivityStepRef.current) {
          lastActivityStepRef.current = tick.step;
          const entries = toActivityEntries(tick);
          if (entries.length) {
            const a = activityRef.current;
            a.push(...entries);
            if (a.length > ACTIVITY_MAX) a.splice(0, a.length - ACTIVITY_MAX);
          }
        }
        publish('running');
      } catch (e) {
        setSnapshot((s) => ({
          ...(s ?? {
            bundle: bundle,
            bundles: bundlesRef.current,
            activeGraphId: activeGraphIdRef.current,
            state: prev,
            balance: buildBalanceSheet(bundle.graph, prev.lastTick),
            metrics: {},
            groupBalance: {},
            groupMetrics: {},
            overlays: { nodeCues: {}, edgeCues: {}, decisions: [] },
          recentTicks: [],
          decisionLog: [],
            status: 'running',
            canUndo: false,
            canRedo: false,
            error: null,
          }),
          error: e instanceof Error ? e.message : String(e),
        }));
        adapter.stop();
      }
    });

    return () => {
      disposed = true;
      unsub();
      unsubBundle?.();
      unsubError?.();
      adapter.dispose();
      adapterRef.current = null;
    };
    // scenarioId + source + relay + editable + hz + api creds are the deps —
    // changing any reloads from scratch (new adapter instance, fresh socket).
  }, [scenarioId, opts.source, opts.relay, opts.editable, opts.hz, opts.apiUrl, opts.apiKey, publish]);

  // Derive the HTTP base URL from the WS URL. The WS URL may carry a path
  // (e.g. ``ws://host:8000/ws``) or query params — strip both, keeping only
  // the origin, so REST calls land at ``http://host:8000/api/...`` rather than
  // ``http://host:8000/ws/api/...`` (which 404s). Captured in a ref so the
  // export/capture/tables callbacks below don't need it in their deps arrays.
  // Empty when no API source is wired.
  const httpBaseRef = useRef<string | null>(null);
  httpBaseRef.current = opts.source === 'api' && opts.apiUrl
    ? httpOriginOf(opts.apiUrl)
    : null;

  // Capture-state poll (Phase E). Low cadence (every 5s) — the badge only
  // needs a coarse tick count, and this avoids spamming the backend. Disabled
  // for local/edit modes (no backend to poll).
  useEffect(() => {
    if (opts.source !== 'api' || !opts.apiUrl) {
      setCaptureState(null);
      return;
    }
    let cancelled = false;
    const base = httpBaseRef.current;
    if (!base) return;
    const headers: Record<string, string> = opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {};
    const poll = async () => {
      try {
        const r = await fetch(`${base}/api/scenarios/${encodeURIComponent(scenarioId)}/capture`, { headers });
        if (!cancelled && r.ok) setCaptureState(await r.json());
      } catch {
        // Network blip — leave the last state; the next tick retries.
      }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [opts.source, opts.apiUrl, opts.apiKey, scenarioId]);

  // Project-model run-status poll. Low cadence (0.5 Hz) — the transport bar's
  // Play/Pause needs to reflect whether the spawned model subprocess is alive,
  // including catching crashes/exits. Disabled for local/edit modes (no backend).
  useEffect(() => {
    if (opts.source !== 'api' || !opts.apiUrl) {
      setRunStatus(null);
      return;
    }
    let cancelled = false;
    const base = httpBaseRef.current;
    if (!base) return;
    const headers: Record<string, string> = opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {};
    const poll = async () => {
      try {
        const r = await fetch(`${base}/api/projects/${encodeURIComponent(scenarioId)}/run`, { headers });
        if (!cancelled && r.ok) setRunStatus(await r.json());
      } catch {
        // Network blip — leave the last state.
      }
    };
    poll();
    const id = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [opts.source, opts.apiUrl, opts.apiKey, scenarioId]);

  // Trigger-status hydration (⚡ management). One fetch on connect — the
  // backend's run-state bookkeeping (last fired step, last error per trigger
  // id); live updates arrive as `triggerEvent` WS frames and merge into the
  // same map. No polling: the frames are the source of truth while connected,
  // this only backfills what happened before the page loaded.
  useEffect(() => {
    if (opts.source !== 'api' || !opts.apiUrl) return;
    const base = httpBaseRef.current;
    if (!base) return;
    let cancelled = false;
    const headers: Record<string, string> = opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {};
    void (async () => {
      try {
        const r = await fetch(
          `${base}/api/projects/${encodeURIComponent(scenarioId)}/workflows/triggers/status`,
          { headers },
        );
        if (!cancelled && r.ok) {
          const data = await r.json() as { triggers?: Record<string, { lastFiredStep: number | null; lastError: string | null }> };
          const incoming = data.triggers ?? {};
          setTriggerStatus((cur) => {
            const next = { ...cur };
            for (const [id, s] of Object.entries(incoming)) {
              next[id] = { lastFiredStep: s.lastFiredStep, lastError: s.lastError, at: Date.now() };
            }
            return next;
          });
        }
      } catch {
        // Network blip — the WS frames keep status fresh anyway.
      }
    })();
    return () => { cancelled = true; };
  }, [opts.source, opts.apiUrl, opts.apiKey, scenarioId]);

  /** POST a tick to the relay webhook. Derives the http(s) base from the WS
   *  URL (ws→http, wss→https). Used by the relay-mode dev "POST test event"
   *  tool; in a real integration the external model POSTs, not the frontend. */

  const start = useCallback(() => {
    adapterRef.current?.start();
    publish('running', true);
  }, [publish]);

  const stop = useCallback(() => {
    adapterRef.current?.stop();
    publish('stopped', true);
  }, [publish]);

  const reset = useCallback(() => {
    adapterRef.current?.reset?.();
    if (bundleRef.current) {
      stateRef.current = initialState(bundleRef.current);
      lastOverlayStepRef.current = -1;
      bnCountsRef.current = new Map();
      decisionsRef.current = [];
      historyRef.current = [];
      activityRef.current = [];
      lastActivityStepRef.current = -1;
      publish('idle', true);
    }
  }, [publish]);

  const setParam = useCallback((path: string, value: number) => {
    adapterRef.current?.setParam(path, value);
  }, []);

  /** Clear the error banner. Errors are transient — the next publish clears
   *  them too — but the in-canvas banner offers an explicit way out. */
  const dismissError = useCallback(() => {
    setSnapshot((s) => (s && s.error ? { ...s, error: null } : s));
  }, []);
  const setParamKey = useCallback(
    (target: 'node' | 'edge', id: string, oldKey: string, newKey: string) =>
      adapterRef.current?.setParamKey?.(target, id, oldKey, newKey),
    [],
  );
  // Undo/redo route through the history-wrapped adapter (only set for
  // editable sessions; demo playback has nothing to undo).
  const undo = useCallback(() => historyAdapterRef.current?.undo(), []);
  const redo = useCallback(() => historyAdapterRef.current?.redo(), []);

  // Editable when the local adapter is in edit mode OR the source is the API
  // (the API backend hosts its own LocalAdapter and accepts the full editor
  // surface over the wire).
  const canEdit = !!opts.editable || opts.source === 'api';
  // The three execution surfaces. `demo` = read-only local compute;
  // `edit` = in-browser LocalAdapter; `api` = WebSocket relay. The UI reads
  // this to grey out knobs in demo mode rather than silently dropping them
  // (read-only local mode ignores setParam, so without this the user gets no
  // feedback that their knob change went nowhere).
  const mode: 'demo' | 'edit' | 'api' | 'relay' =
    opts.source === 'api' ? (opts.relay ? 'relay' : 'api') : opts.editable ? 'edit' : 'demo';
  const addNode = useCallback((n: GraphNode) => adapterRef.current?.addNode?.(n), []);
  const updateNode = useCallback((n: GraphNode) => adapterRef.current?.updateNode?.(n), []);
  // ── Multi-graph (graph collection) ──────────────────────────────────────
  // Switching the active graph is local-only state (not broadcast); the next
  // per-graph edit carries the new id. add/remove/rename are collection ops.
  const setActiveGraph = useCallback((graphId: string) => {
    const adapter = adapterRef.current;
    if (!adapter) return;
    adapter.setActiveGraph?.(graphId);
    const id = adapter.activeGraphId?.() ?? graphId;
    activeGraphIdRef.current = id;
    const bundle = bundlesRef.current.find((b) => b.id === id) ?? adapter.exportBundle?.();
    if (bundle) {
      bundleRef.current = bundle;
      // Re-seed sim state for the newly-active graph's nodes (default 0 stock
      // for nodes the prior graph didn't have; keep any shared ids' values).
      const stocks = { ...(bundle.initialStocks ?? {}) };
      for (const n of bundle.graph.nodes) if (!(n.id in stocks)) stocks[n.id] = 0;
      stateRef.current = {
        step: stateRef.current?.step ?? 0,
        stocks,
        lastTick: stateRef.current?.lastTick ?? { step: 0, nodeValues: {}, edgeFlows: {} },
      };
      lastRenderRef.current = 0;
      publish('idle', true);
    }
  }, [publish]);
  const addGraph = useCallback((name: string) => adapterRef.current?.addGraph?.(name) ?? 'main', []);
  const removeGraph = useCallback((graphId: string) => adapterRef.current?.removeGraph?.(graphId), []);
  const renameGraph = useCallback(
    (graphId: string, name: string) => adapterRef.current?.renameGraph?.(graphId, name) ?? graphId,
    [],
  );
  const removeNode = useCallback((id: string) => adapterRef.current?.removeNode?.(id), []);
  const duplicateNode = useCallback(
    (id: string) => adapterRef.current?.duplicateNode?.(id) ?? null,
    [],
  );
  const renameNode = useCallback(
    (oldId: string, newId: string) => adapterRef.current?.renameNode?.(oldId, newId) ?? false,
    [],
  );
  const addEdge = useCallback((e: GraphEdge) => adapterRef.current?.addEdge?.(e), []);
  const updateEdge = useCallback((e: GraphEdge) => adapterRef.current?.updateEdge?.(e), []);
  const removeEdge = useCallback((id: string) => adapterRef.current?.removeEdge?.(id), []);
  const renameEdge = useCallback(
    (oldId: string, newId: string) => adapterRef.current?.renameEdge?.(oldId, newId) ?? false,
    [],
  );
  const setEdgeGroup = useCallback(
    (id: string, group: string | undefined) => adapterRef.current?.setEdgeGroup?.(id, group),
    [],
  );
  const updateEdgeHandles = useCallback(
    (
      id: string,
      next: { source?: string; target?: string; sourceHandle?: string; targetHandle?: string },
    ) => adapterRef.current?.updateEdgeHandles?.(id, next),
    [],
  );
  const addGroup = useCallback((g: string) => adapterRef.current?.addGroup?.(g), []);
  const addNodeGroup = useCallback(
    (g: NodeGroup) => adapterRef.current?.addNodeGroup?.(g),
    [],
  );
  const updateNodeGroup = useCallback(
    (g: NodeGroup) => adapterRef.current?.updateNodeGroup?.(g),
    [],
  );
  const removeNodeGroup = useCallback(
    (id: string) => adapterRef.current?.removeNodeGroup?.(id),
    [],
  );
  const renameGroup = useCallback(
    (oldId: string, newId: string) => adapterRef.current?.renameGroup?.(oldId, newId) ?? false,
    [],
  );
  const setBends = useCallback(
    (id: string, bends: import('./engine/types').Bend[]) => adapterRef.current?.setBends?.(id, bends),
    [],
  );
  const setLabelT = useCallback(
    (id: string, t: number) => adapterRef.current?.setLabelT?.(id, t),
    [],
  );
  const addMetric = useCallback((m: MetricSpec) => adapterRef.current?.addMetric?.(m), []);
  const updateMetric = useCallback((m: MetricSpec) => adapterRef.current?.updateMetric?.(m), []);
  const removeMetric = useCallback((k: string) => adapterRef.current?.removeMetric?.(k), []);
  const renameMetric = useCallback(
    (oldKey: string, newKey: string) => adapterRef.current?.renameMetric?.(oldKey, newKey),
    [],
  );
  const setLegend = useCallback(
    (legend: LegendEntry[]) => adapterRef.current?.setLegend?.(legend),
    [],
  );
  const setKnobs = useCallback(
    (knobs: KnobPin[]) => adapterRef.current?.setKnobs?.(knobs),
    [],
  );
  const setWorkflowTriggers = useCallback(
    (triggers: WorkflowTrigger[]) => adapterRef.current?.setWorkflowTriggers?.(triggers),
    [],
  );
  const replaceBundle = useCallback(
    (b: ScenarioBundle) => adapterRef.current?.replaceBundle?.(b),
    [],
  );
  const exportBundle = useCallback((): ScenarioBundle | undefined => {
    const b = adapterRef.current?.exportBundle?.();
    return b;
  }, []);

  /** POST a tick to the relay webhook. Derives the http(s) origin from the WS
   *  URL via ``httpBaseRef`` (ws→http, path stripped). Used by the relay-mode
   *  dev "POST test event" tool; in a real integration the external model
   *  POSTs, not the frontend. */
  const postEvent = useCallback(
    async (tick: TickPayload): Promise<number | null> => {
      if (opts.source !== 'api' || !opts.apiUrl) return null;
      const base = httpBaseRef.current;
      if (!base) return null;
      const url = `${base}/api/events/${encodeURIComponent(scenarioId)}`;
      try {
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...(opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {}) },
          body: JSON.stringify(tick),
        });
        return r.status;
      } catch {
        return null;
      }
    },
    [opts.source, opts.apiUrl, opts.apiKey, scenarioId],
  );

  /** Export the captured/replayed run (Phase E). Fetches from the backend's
   *  export endpoint and triggers a browser download. Returns the HTTP status
   *  or null when no API source is wired. */
  const exportRun = useCallback(
    async (
      format: 'wide' | 'long' | 'trajectory',
      xopts: { from?: number; to?: number; reward?: string } = {},
    ): Promise<number | null> => {
      const base = httpBaseRef.current;
      if (!base) return null;
      const params = new URLSearchParams({ format });
      if (xopts.from != null) params.set('from', String(xopts.from));
      if (xopts.to != null) params.set('to', String(xopts.to));
      if (xopts.reward) params.set('reward', xopts.reward);
      const url = `${base}/api/scenarios/${encodeURIComponent(scenarioId)}/export?${params}`;
      try {
        const r = await fetch(url, {
          headers: opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {},
        });
        if (!r.ok) return r.status;
        // Trigger a download. The Content-Disposition header carries the
        // filename; fall back to a format-derived name when absent.
        const disp = r.headers.get('content-disposition') ?? '';
        const m = /filename="?([^";]+)"?/.exec(disp);
        const filename = m?.[1] ?? `${scenarioId}_${format}.${format === 'trajectory' ? 'jsonl' : 'csv'}`;
        const blob = await r.blob();
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(u);
        return r.status;
      } catch {
        return null;
      }
    },
    [scenarioId, opts.apiKey],
  );

  /** Download the project as a ZIP (graph + manifest + code). Fetches from the
   *  backend's /api/projects/{id}/download endpoint + triggers a browser save.
   *  Returns the HTTP status or null when no API source is wired. */
  const downloadProject = useCallback(
    async (): Promise<number | null> => {
      const base = httpBaseRef.current;
      if (!base) return null;
      // The project id may differ from the scenarioId (underscore vs hyphen).
      // The backend's /api/projects resolves both, so pass scenarioId directly.
      const url = `${base}/api/projects/${encodeURIComponent(scenarioId)}/download`;
      try {
        const r = await fetch(url, {
          headers: opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {},
        });
        if (!r.ok) return r.status;
        const disp = r.headers.get('content-disposition') ?? '';
        const m = /filename="?([^";]+)"?/.exec(disp);
        const filename = m?.[1] ?? `${scenarioId}.zip`;
        const blob = await r.blob();
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(u);
        return r.status;
      } catch {
        return null;
      }
    },
    [scenarioId, opts.apiKey],
  );

  /** Arm/disarm/clear the live recording (Phase E + datasets — docs/
   *  TRAINING.md §4). No-op without an API source. After arming, the poll
   *  effect above picks up the new state. Persistence (the durable dataset
   *  run) is decided by the project manifest's ``record.saveDataset``
   *  (default true) — not a client concern. `opts.label` names the run (the
   *  Record dialog) — stored into the registry meta the replay picker shows. */
  const setCapture = useCallback(
    async (action: 'arm' | 'disarm' | 'clear', arm?: { label?: string }): Promise<void> => {
      const base = httpBaseRef.current;
      if (!base) return;
      try {
        const label = arm?.label?.trim();
        const r = await fetch(
          `${base}/api/scenarios/${encodeURIComponent(scenarioId)}/capture?action=${action}`,
          {
            method: 'POST',
            headers: {
              ...(label ? { 'content-type': 'application/json' } : {}),
              ...(opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {}),
            },
            body: label ? JSON.stringify({ action, label }) : undefined,
          },
        );
        if (r.ok) setCaptureState(await r.json());
      } catch {
        // Network blip — the next poll retries.
      }
    },
    [scenarioId, opts.apiKey],
  );

  // ─── Project tables + model run control ────────────────────────────────
  // Tables: GET /api/projects/{id}/tables, PATCH /api/projects/{id}/tables/{t}/{rowId}.
  // Run:    POST /api/projects/{id}/run/{start,stop}, GET /api/projects/{id}/run.
  // All reuse the httpBaseRef + bearer pattern.

  /** Extract a human-readable error from a failed tables request. */
  const tablesErrText = async (r: Response, fallback: string): Promise<string> => {
    try {
      const j = await r.json();
      return String(j.detail ?? j.message ?? fallback);
    } catch {
      return fallback;
    }
  };

  /** Load all project tables from the backend. No-op without an API source. */
  const loadTables = useCallback(async (): Promise<void> => {
    const base = httpBaseRef.current;
    if (!base) return;
    try {
      const r = await fetch(`${base}/api/projects/${encodeURIComponent(scenarioId)}/tables`, {
        headers: opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {},
      });
      if (r.ok) {
        setTablesState((await r.json()).tables);
      } else {
        setTablesError(await tablesErrText(r, `Loading tables failed (${r.status}).`));
      }
    } catch {
      // Network blip — leave the last state.
    }
  }, [scenarioId, opts.apiKey]);

  // Periodic refresh while enabled (panel visible / a db knob exists): the
  // model mutates rows behind the grid's back, so the snapshot (and the db
  // knob sliders reading it) go stale otherwise. Backs off right after a
  // mutation so in-flight edits aren't clobbered.
  const loadTablesRef = useRef(loadTables);
  loadTablesRef.current = loadTables;
  useEffect(() => {
    const id = window.setInterval(() => {
      if (
        tablesPollRef.current &&
        httpBaseRef.current &&
        Date.now() - tablesActivityRef.current > 1500
      ) {
        void loadTablesRef.current();
      }
    }, 3000);
    return () => window.clearInterval(id);
  }, []);

  // Cell-bound params (ParamSpec.binding / edge.weightBinding): feed the
  // latest tables snapshot to the local engine so it resolves bound values
  // into its param overlays each tick. The ApiAdapter ignores this — in relay
  // mode the BACKEND resolves bindings per tick and ships the values in
  // tick.params / paramChange frames.
  useEffect(() => {
    adapterRef.current?.updateTableSnapshots?.(tablesState ?? null);
  }, [tablesState]);
  /** PATCH one project table cell. Refreshes the affected table in the local
   *  snapshot so the UI updates immediately without a full reload. */
  const updateTableCell = useCallback(
    async (table: string, rowId: number | string, column: string, value: string | number | boolean | null): Promise<void> => {
      const base = httpBaseRef.current;
      if (!base) return;
      tablesActivityRef.current = Date.now();
      try {
        const r = await fetch(
          `${base}/api/projects/${encodeURIComponent(scenarioId)}/tables/${encodeURIComponent(table)}/${rowId}`,
          {
            method: 'PATCH',
            headers: {
              'content-type': 'application/json',
              ...(opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {}),
            },
            body: JSON.stringify({ column, value }),
          },
        );
        if (r.ok) {
          setTablesError(null);
          // The response is the updated table snapshot; splice it in.
          const updated = await r.json();
          setTablesState((cur) => (cur ? cur.map((t) => (t.name === table ? updated : t)) : cur));
        } else {
          setTablesError(await tablesErrText(r, `Updating ${table}.${column} failed (${r.status}).`));
        }
      } catch {
        setTablesError(`Updating ${table}.${column} failed (network).`);
      }
    },
    [scenarioId, opts.apiKey],
  );

  /** Create a new editable project table. */
  const addTable = useCallback(
    async (name: string, columns: { name: string; type: string; required?: boolean }[]): Promise<void> => {
      const base = httpBaseRef.current;
      if (!base) return;
      tablesActivityRef.current = Date.now();
      try {
        const r = await fetch(`${base}/api/projects/${encodeURIComponent(scenarioId)}/tables`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            ...(opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {}),
          },
          body: JSON.stringify({ name, columns }),
        });
        if (r.ok) {
          setTablesError(null);
          const newTable = await r.json();
          setTablesState((cur) => (cur ? [...cur, newTable] : [newTable]));
        } else {
          setTablesError(await tablesErrText(r, `Creating table '${name}' failed (${r.status}).`));
        }
      } catch {
        setTablesError(`Creating table '${name}' failed (network).`);
      }
    },
    [scenarioId, opts.apiKey],
  );

  /** Insert a new row into an editable project table. */
  const addTableRow = useCallback(
    async (table: string, cells: Record<string, string | number | boolean | null>): Promise<void> => {
      const base = httpBaseRef.current;
      if (!base) return;
      tablesActivityRef.current = Date.now();
      try {
        const r = await fetch(`${base}/api/projects/${encodeURIComponent(scenarioId)}/tables/${encodeURIComponent(table)}`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            ...(opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {}),
          },
          body: JSON.stringify({ cells }),
        });
        if (r.ok) {
          setTablesError(null);
          const updated = await r.json();
          setTablesState((cur) => (cur ? cur.map((t) => (t.name === table ? updated : t)) : cur));
        } else {
          setTablesError(await tablesErrText(r, `Inserting into '${table}' failed (${r.status}).`));
        }
      } catch {
        setTablesError(`Inserting into '${table}' failed (network).`);
      }
    },
    [scenarioId, opts.apiKey],
  );

  /** Delete a row from an editable project table. */
  const deleteTableRow = useCallback(
    async (table: string, rowId: number | string): Promise<void> => {
      const base = httpBaseRef.current;
      if (!base) return;
      tablesActivityRef.current = Date.now();
      try {
        const r = await fetch(`${base}/api/projects/${encodeURIComponent(scenarioId)}/tables/${encodeURIComponent(table)}/${rowId}`, {
          method: 'DELETE',
          headers: {
            ...(opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {}),
          },
        });
        if (r.ok) {
          setTablesError(null);
          const updated = await r.json();
          setTablesState((cur) => (cur ? cur.map((t) => (t.name === table ? updated : t)) : cur));
        } else {
          setTablesError(await tablesErrText(r, `Deleting the row failed (${r.status}).`));
        }
      } catch {
        setTablesError(`Deleting the row failed (network).`);
      }
    },
    [scenarioId, opts.apiKey],
  );

  /** Start the project's model subprocess. The spawned model POSTs ticks to
   *  the relay webhook; subscribers see them as normal relay ticks. Updates
   *  runStatus from the response so the transport bar flips immediately. */
  const runStart = useCallback(async (): Promise<void> => {
    const base = httpBaseRef.current;
    if (!base) return;
    try {
      const r = await fetch(`${base}/api/projects/${encodeURIComponent(scenarioId)}/run/start`, {
        method: 'POST',
        headers: opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {},
      });
      if (r.ok) setRunStatus(await r.json());
    } catch {
      // Network blip — the next poll resyncs.
    }
  }, [scenarioId, opts.apiKey]);

  /** Stop the project's model subprocess. Updates runStatus from the response. */
  const runStop = useCallback(async (): Promise<void> => {
    const base = httpBaseRef.current;
    if (!base) return;
    try {
      const r = await fetch(`${base}/api/projects/${encodeURIComponent(scenarioId)}/run/stop`, {
        method: 'POST',
        headers: opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {},
      });
      if (r.ok) setRunStatus(await r.json());
    } catch {
      // Network blip — the next poll resyncs.
    }
  }, [scenarioId, opts.apiKey]);

  /** Toggle the run-level agents kill-switch (docs/TRAINING.md §2 — the
   *  emergency stop for agent writes). When disabled the relay path skips
   *  agent application entirely; ticks pass through unmodified. Optimistic
   *  update — the 2s run-status poll resyncs if the request fails. */
  const toggleAgents = useCallback(async (enabled: boolean): Promise<void> => {
    const base = httpBaseRef.current;
    if (!base) return;
    setRunStatus((prev) => (prev ? { ...prev, agentsEnabled: enabled } : prev));
    try {
      const r = await fetch(`${base}/api/projects/${encodeURIComponent(scenarioId)}/agents/toggle`, {
        method: 'POST',
        headers: {
          ...(opts.apiKey ? { authorization: `Bearer ${opts.apiKey}` } : {}),
          'content-type': 'application/json',
        },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error(String(r.status));
    } catch {
      // Network blip — the next poll resyncs the true state.
    }
  }, [scenarioId, opts.apiKey]);

  // Until the snapshot exists, return a loading sentinel. The consumer guards.
  if (!snapshot) {
    return {
      bundle: null as unknown as ScenarioBundle,
      bundles: [],
      activeGraphId: 'main',
      state: null as unknown as ModelState,
      balance: null as unknown as BalanceSheet,
      metrics: {},
      groupBalance: {},
      groupMetrics: {},
      overlays: { nodeCues: {}, edgeCues: {}, decisions: [] },
      recentTicks: [],
      decisionLog: [],
      status: 'idle',
      canUndo: false,
      canRedo: false,
      error: null,
      start,
      stop,
      reset,
      dismissError,
      setParam,
      canEdit,
      mode,
      addNode,
      updateNode,
      removeNode,
      duplicateNode,
      renameNode,
      addEdge,
      updateEdge,
      removeEdge,
      renameEdge,
      setEdgeGroup,
      updateEdgeHandles,
      setBends,
      setLabelT,
      addGroup,
      addNodeGroup,
      updateNodeGroup,
      removeNodeGroup,
      renameGroup,
      addMetric,
      updateMetric,
      removeMetric,
      renameMetric,
      setLegend,
      setWorkflowTriggers,
      setKnobs,
      replaceBundle,
      setParamKey,
      exportBundle,
      setActiveGraph,
      addGraph,
      removeGraph,
      renameGraph,
      postEvent,
      exportRun,
      downloadProject,
      setCapture,
      captureState,
      tablesState,
      tablesError,
      clearTablesError,
      setTablesPolling,
      loadTables,
      updateTableCell,
      addTable,
      addTableRow,
      deleteTableRow,
      runStart,
      runStop,
      toggleAgents: async () => undefined,
      runStatus,
      connectionStatus,
      // ⚡ trigger surfaces — safe stubs on the loading path (no adapter
      // wired yet; the real ones arrive with the first snapshot).
      onTriggerEvent: () => () => undefined,
      triggerStatus: {},
    };
  }

  return {
    ...snapshot,
    start,
    stop,
    reset,
    dismissError,
    setParam,
    canEdit,
    mode,
    addNode,
    updateNode,
    removeNode,
    duplicateNode,
    renameNode,
    addEdge,
    updateEdge,
    removeEdge,
    renameEdge,
    setEdgeGroup,
    updateEdgeHandles,
    setBends,
    setLabelT,
    addGroup,
    addNodeGroup,
    updateNodeGroup,
    removeNodeGroup,
    renameGroup,
    addMetric,
    updateMetric,
    removeMetric,
    renameMetric,
    setLegend,
    setWorkflowTriggers,
    setKnobs,
    replaceBundle,
    setParamKey,
    undo,
    redo,
    exportBundle,
    setActiveGraph,
    addGraph,
    removeGraph,
    renameGraph,
    postEvent,
    exportRun,
    downloadProject,
    setCapture,
    captureState,
    tablesState,
    tablesError,
    clearTablesError,
    setTablesPolling,
    loadTables,
    updateTableCell,
    addTable,
    addTableRow,
    deleteTableRow,
    runStart,
    runStop,
    /** Toggle the run-level agents kill-switch (emergency stop for agent
     *  writes). API/relay mode only. */
    toggleAgents,
    runStatus,
    connectionStatus,
    /** Subscribe to workflow step events (worker-gate I/O captures). Returns
     *  an unsubscribe fn. API/relay mode only — local adapters never emit. */
    onWorkflowEvent,
    /** Subscribe to trigger fire/fail events (the relay path's ⚡ fire
     *  attempts). Returns an unsubscribe fn. API/relay mode only. */
    onTriggerEvent,
    /** Live ⚡ trigger status: {triggerId: {lastFiredStep, lastError, at}}.
     *  Updated by `triggerEvent` frames; hydrated once on connect from the
     *  backend's run-state bookkeeping. Read by the TriggersPanel + badges. */
    triggerStatus,
  };
}
